import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import featureService from '../services/featureService';

const FeatureContext = createContext();

export const useFeatures = () => {
  const ctx = useContext(FeatureContext);
  if (!ctx) throw new Error('useFeatures must be used within a FeatureProvider');
  return ctx;
};

const GATED = ['workbooks', 'vera', 'sentinel', 'inventory'];

/**
 * Loads the current org's feature entitlements once after auth. Default-open
 * while loading / on error (matches the backend's fail-open posture) so a
 * transient fetch failure never hides a feature the org actually has.
 */
export const FeatureProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [features, setFeatures] = useState({ workbooks: true, vera: true, sentinel: true, inventory: true });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await featureService.getMyFeatures();
      setFeatures((prev) => ({ ...prev, ...data }));
    } catch (e) {
      // Fail open — keep optimistic defaults.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setFeatures({ workbooks: true, vera: true, sentinel: true, inventory: true });
    }
  }, [isAuthenticated, refresh]);

  // A feature not in the gated set is always enabled.
  const isFeatureEnabled = useCallback(
    (name) => !GATED.includes(name) || features[name] !== false,
    [features]
  );

  return (
    <FeatureContext.Provider value={{ features, loading, isFeatureEnabled, refresh }}>
      {children}
    </FeatureContext.Provider>
  );
};

export default FeatureContext;
